// This file is kind of nuts but its very flexible and easy to use.
import * as Phaser from 'phaser';
import type Phrase from 'Models/Phrase';
import { ToPhaserEvents, type OSMDPhraseDataEventPayload } from './ToPhaser';
import { ToReactEvents } from './ToReact';

export type { StartPlayingArgs, AdaptiveTempoState } from './ToReact';
export type { OSMDPhraseDataEventPayload, AppTourStepPhaser, MidiOnPayload } from './ToPhaser';

/** Create an AddPhrase event with OSMD payload (MusicXML). Used by repertoire/OSMD containers. */
export function createAddOSMDPhraseEvent(
  phraseRaw: Phrase,
  showClef: boolean = true,
  showBrace: boolean = true,
  showTimeSig: boolean = true,
): [keyof ToPhaserEvents, OSMDPhraseDataEventPayload] {
  const payload: OSMDPhraseDataEventPayload = {
    phraseRaw,
    phrase: {
      musicXml: phraseRaw.musicXML.xml,
      uuid: phraseRaw.uuid,
      startTimestamp: phraseRaw.getStartTimestamp(),
      timeSignature: {
        numerator: phraseRaw.musicXML.timeSignatures[0]?.numerator ?? 4,
        denominator: phraseRaw.musicXML.timeSignatures[0]?.denominator ?? 4,
      },
      showClef,
      showBrace,
      showTimeSig,
    },
  };
  return ['AddPhraseOSMD', payload];
}

type EventsTuple<T extends Record<string, unknown>, K extends keyof T> = [K, T[K]];
type EventListenerTuple<T extends Record<string, unknown>, K extends keyof T> = [
  K,
  (payload: T[K]) => void,
];

type EventCreator<T extends Record<string, unknown>, K extends keyof T> = (
  arg: T[K],
) => EventsTuple<T, K>;

type EventListenerCreator<T extends Record<string, unknown>, K extends keyof T> = (
  callback: (payload: T[K]) => void,
) => EventListenerTuple<T, K>;

type SendEventsType<T extends Record<string, unknown>> = {
  [K in keyof T]: EventCreator<T, K>;
};

type ListenEventsType<T extends Record<string, unknown>> = {
  [K in keyof T]: EventListenerCreator<T, K>;
};

/*function createSendEvents<T extends Record<string, unknown>>(): {
  [K in keyof T]: (arg: T[K]) => [K, T[K]];
} {
  // This is a Proxy-based approach so you don't have to manually list events
  return new Proxy({} as SendEventsType<T>, {
    get(_target, key: string) {
      return (arg: unknown) => [key, arg];
    },
  });
}*/

/*function createListenEvents<T extends Record<string, unknown>>(): {
  [K in keyof T]: (arg: (payload: T[K]) => void) => [K, (payload: T[K]) => void];
} {
  return new Proxy({} as ListenEventsType<T>, {
    get(_target, key: string) {
      return (callback: (payload: T[keyof T]) => void) => [key, callback];
    },
  });
}*/

function createSendEvents<T extends Record<string, unknown>>(
  fromPhaser?: boolean,
): { [K in keyof T]: (arg: T[K]) => /*[K, T[K]]*/ void } {
  // This is a Proxy-based approach so you don't have to manually list events
  return new Proxy({} as SendEventsType<T>, {
    get(_target, key: string) {
      if (fromPhaser) {
        return (arg: unknown) =>
          NotifyReact([key as keyof ToReactEvents, arg as ToReactEvents[keyof ToReactEvents]]);
      } else {
        return (arg: unknown) =>
          NotifyPhaser([key as keyof ToPhaserEvents, arg as ToPhaserEvents[keyof ToPhaserEvents]]);
      }
    },
  });
}

function createListenEvents<T extends Record<string, unknown>>(
  fromPhaser?: boolean,
): {
  [K in keyof T]: (
    arg: (payload: T[K]) => void,
    context?: object,
    once?: boolean,
    collectWaiting?: boolean,
  ) => void;
} {
  return new Proxy({} as ListenEventsType<T>, {
    get(_target, key: string) {
      if (fromPhaser) {
        return (
          callback: (payload: T[keyof T]) => void,
          context?: object,
          once?: boolean,
          collectWaiting?: boolean,
        ) =>
          ReactListenForPhaser(
            [
              key as keyof ToReactEvents,
              callback as (payload: ToReactEvents[keyof ToReactEvents]) => void,
            ],
            context,
            once,
            collectWaiting,
          );
      } else {
        return (
          callback: (payload: T[keyof T]) => void,
          context?: object,
          once?: boolean,
          collectWaiting?: boolean,
        ) =>
          PhaserListenForReact(
            [
              key as keyof ToPhaserEvents,
              callback as (payload: ToPhaserEvents[keyof ToPhaserEvents]) => void,
            ],
            context,
            once,
            collectWaiting,
          );
      }
    },
  });
}

const messageQueuePhaserToReact = new Map<
  keyof ToReactEvents,
  [keyof ToReactEvents, ToReactEvents[keyof ToReactEvents]][]
>();

const messageQueueReactToPhaser = new Map<
  keyof ToPhaserEvents,
  [keyof ToPhaserEvents, ToPhaserEvents[keyof ToPhaserEvents]][]
>();

export const EventBus = new Phaser.Events.EventEmitter();

export const NotifyPhaserEvents = createSendEvents<ToPhaserEvents>();
export const ReactToPhaserListeners = createListenEvents<ToPhaserEvents>();
export const NotifyReactEvents = createSendEvents<ToReactEvents>();
export const PhaserToReactListeners = createListenEvents<ToReactEvents>(true);

/** Union of all React→Phaser event tuples. Use for typing event payloads. */
export type ReactToPhaserEvent = {
  [K in keyof ToPhaserEvents]: [K, ToPhaserEvents[K]];
}[keyof ToPhaserEvents];

export function NotifyReact<T extends keyof ToReactEvents>([evType, ev]: EventsTuple<
  ToReactEvents,
  T
>): void {
  const listenerCount = EventBus.listeners(evType).length;
  if (listenerCount === 0) {
    console.warn('[EventBus::no_listeners] no listeners registered for event', evType);
    const queue = messageQueuePhaserToReact.get(evType);
    if (queue) {
      messageQueuePhaserToReact.set(evType, [...queue, [evType, ev]]);
    } else {
      messageQueuePhaserToReact.set(evType, [[evType, ev]]);
    }
  } else {
    EventBus.emit(evType, ev);
  }
}

export function NotifyPhaser<T extends keyof ToPhaserEvents>([evType, ev]: EventsTuple<
  ToPhaserEvents,
  T
>): void {
  const listenerCount = EventBus.listeners(evType).length;
  if (listenerCount === 0) {
    console.warn('[EventBus::no_listeners] no listeners registered for event', evType);
    const queue = messageQueueReactToPhaser.get(evType);
    if (queue) {
      messageQueueReactToPhaser.set(evType, [...queue, [evType, ev]]);
    } else {
      messageQueueReactToPhaser.set(evType, [[evType, ev]]);
    }
  } else {
    EventBus.emit(evType, ev);
  }
}

export function ClearQueueReact<T extends keyof ToReactEvents>(eventType?: T): void {
  if (eventType && messageQueuePhaserToReact.has(eventType)) {
    messageQueuePhaserToReact.delete(eventType);
  } else {
    messageQueuePhaserToReact.clear();
  }
}

export function ClearQueuePhaser<T extends keyof ToPhaserEvents>(eventType?: T): void {
  if (eventType && messageQueueReactToPhaser.has(eventType)) {
    messageQueueReactToPhaser.delete(eventType);
  } else {
    messageQueueReactToPhaser.clear();
  }
}

export function PhaserListenForReact<T extends keyof ToPhaserEvents>(
  cbTuple: EventListenerTuple<ToPhaserEvents, T>,
  context?: object,
  once?: boolean,
  collectWaiting?: boolean,
  //options?: { once?: boolean; context?: object },
): void {
  const [eventType, cb] = cbTuple;
  //const context = options?.context;
  if (collectWaiting) {
    const queue = messageQueueReactToPhaser.get(eventType);
    if (queue) {
      for (const [_, payload] of queue) {
        if (context !== undefined) {
          (cb as (p: ToPhaserEvents[T]) => void).call(context, payload as ToPhaserEvents[T]);
        } else {
          cb(payload as ToPhaserEvents[T]);
        }
      }
      queue.length = 0;
    }
  }
  if (once) {
    EventBus.once(eventType, cb, context);
  } else {
    EventBus.on(eventType, cb, context);
  }
}

export function ReactListenForPhaser<T extends keyof ToReactEvents>(
  cbTuple: EventListenerTuple<ToReactEvents, T>,
  context?: object,
  once?: boolean,
  collectWaiting?: boolean,
  //options?: { once?: boolean; context?: object },
): void {
  const [eventType, cb] = cbTuple;
  //const context = options?.context;
  if (collectWaiting) {
    const queue = messageQueuePhaserToReact.get(eventType);
    if (queue) {
      for (const [_, payload] of queue) {
        if (context !== undefined) {
          (cb as (p: ToReactEvents[T]) => void).call(context, payload as ToReactEvents[T]);
        } else {
          cb(payload as ToReactEvents[T]);
        }
      }
      queue.length = 0;
    }
  }
  if (once) {
    EventBus.once(eventType, cb, context);
  } else {
    EventBus.on(eventType, cb, context);
  }
}

export function RemoveListener(event: keyof ToPhaserEvents | keyof ToReactEvents): void;
export function RemoveListener<K extends keyof ToReactEvents>(
  event: K,
  fn: (payload: ToReactEvents[K]) => void,
): void;
export function RemoveListener<K extends keyof ToPhaserEvents>(
  event: K,
  fn: (payload: ToPhaserEvents[K]) => void,
): void;
export function RemoveListener(
  event: keyof ToPhaserEvents | keyof ToReactEvents,
  fn?: ((payload: unknown) => void) | ((...args: unknown[]) => void),
): void {
  console.warn(
    '[EventBus::RemoveListener] removing listener for event',
    event,
    EventBus.listeners('SetAdaptiveTempoState'),
  );
  EventBus.off(event, fn);
}
